import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChevronRight,
  Eye,
  Download,
  Calendar,
  ArrowUp,
  ArrowDown,
  MoreHorizontal,
  Search,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  ChevronsRight,
  FileText,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useDebounce } from "@/hooks/use-debounce";
import { toast } from "sonner";
import { handleAuthError } from "@/utils/auth";
import { getFormattedDateTime } from "@/utils";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { InvoiceStats } from "@/components/invoices/InvoiceStats";
import { RewardPointInvoiceFilters } from "@/components/rewardpoint/RewardPointInvoiceFilters";
import { InvoiceTableSkeleton } from "@/components/invoices/InvoiceTableSkeleton";
import { InvoiceDetailsModal } from "@/components/invoices/InvoiceDetailsModal";
import { downloadResponseFile } from "@/utils/common.utils";
import { InvoiceApi, InvoiceVO } from "@/api/invoice.api";
import { APIClient } from "@/api/axiosClient";

interface Invoice {
  id: string;
  merchant: string;
  program: string;
  issueDate: string;
  dueDate: string;
  paidDate?: string;
  amount: number;
  status: "paid" | "pending" | "overdue" | string;
  memberName: string;
  memberId: string;
  memberEmail: string;
  merchantAddress: string;
  merchantEmail: string;
  paymentMethod: string;
  subtotal: number;
  taxRate: number;
  tax: number;
  total: number;
  startDate: string | null;
  endDate: string | null;
  transactions: Array<{
    id: string;
    date: string;
    description: string;
    points: number;
    amount: number;
  }>;
  createdDate: string;
}

interface InvoiceResponse {
  invoiceVOS: Invoice[];
  totalRecords: number;
}

interface Column {
  header: string | React.ReactNode;
  accessorKey?: keyof Invoice;
  id?: string;
  cell?: (row: Invoice) => React.ReactNode;
}

const DELAY = 500;

const formatCurrency = (value: number) => {
  // Truncate to 2 decimal places without rounding
  const truncated = Math.floor(value * 100) / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(truncated);
};

const formatNumber = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatDate = (dateString: string) => {
  if (!dateString) return "N/A";
  return getFormattedDateTime(dateString) || "N/A";
};

const getDateRangeMillis = (range) => {
  const now = new Date();
  let start, end;
  switch (range) {
    case "30days":
      start = new Date(now);
      start.setDate(now.getDate() - 29);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
      break;
    case "7days":
      start = new Date(now);
      start.setDate(now.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
      break;
    case "today":
      start = new Date(now);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
      break;
    case "yesterday":
      start = new Date(now);
      start.setDate(now.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setDate(now.getDate() - 1);
      end.setHours(23, 59, 59, 999);
      break;
    case "thisMonth":
      start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
      break;
    case "lastMonth":
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      break;
    case "none":
      start = null;
      end = null;
      break;
    default:
      start = null;
      end = null;
  }
  return {
    startDateInMillis: range === "none" ? null : start ? start.getTime() : null,
    endDateInMillis: range === "none" ? null : end ? end.getTime() : null,
  };
};

const RewardPointInvoices = () => {
  const navigate = useNavigate();
  const { refreshToken } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<{
    status?: string;
    programCode?: string;
    merchantCode?: string;
    id?: string;
    startDate?: string;
    endDate?: string;
  }>({});
  const [dateRange, setDateRange] = useState("none");
  const [sortBy, setSortBy] = useState<{
    field: string;
    direction: "asc" | "desc";
  }>({ field: "createdDate", direction: "desc" });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [invoiceStats, setInvoiceStats] = useState({
    totalInvoices: 0,
    totalPaid: "$0.00",
    pendingAmount: "$0.00",
    overdueAmount: "$0.00",
    averagePaymentTime: 0,
    totalPaidInvoices: 0,
    totalPendingInvoices: 0,
    totalValueOfAllInvoices: "$0.00",
  });
  const [programs, setPrograms] = useState([]);
  const [redemptionMerchants, setRedemptionMerchants] = useState([]);

  const handleApiError = async (response: Response) => {
    return handleAuthError(response, refreshToken);
  };

  const fetchInvoices = async () => {
    try {
      setIsLoading(true);
      let startDateInMillis = null;
      let endDateInMillis = null;
      if (filters.startDate && filters.endDate) {
        startDateInMillis = new Date(filters.startDate).getTime();
        endDateInMillis = new Date(filters.endDate).getTime();
      } else {
        const range = getDateRangeMillis(dateRange);
        startDateInMillis = range.startDateInMillis;
        endDateInMillis = range.endDateInMillis;
      }
      const response = await InvoiceApi.getInvoicesFromDB({
        invoiceType: "burn",
        pageNumber: currentPage,
        pageSize: rowsPerPage,
        sortBy: sortBy.field,
        sortOrder: sortBy.direction,
        ...filters,
        startDateInMillis,
        endDateInMillis,
      });

      if (response.data) {
        // Transform API data to match the expected format (using new sample response)
        const transformedInvoices = response.data.invoiceVOs.map((invoice) => {
          // Use the new sample response fields, but keep the same UI column names
          return {
            id: `#${invoice.id}`,
            createdDate: invoice.createdDate || "N/A",
            merchant: invoice.from || "N/A", // Invoice From
            program:
              invoice.to || invoice.transferPartnerVO?.partnerName || "N/A", // Invoice To
            issueDate: invoice.createdDate || "N/A",
            dueDate: invoice.dueDate || "N/A",
            amount: invoice.amount || 0,
            status: (invoice.status || "PENDING").toLowerCase(),
            memberName: invoice.transferPartnerVO?.partnerName || "N/A", // fallback for memberName
            memberId: invoice.transferPartnerVO?.partnerCode || "N/A", // fallback for memberId
            memberEmail:
              invoice.transferPartnerVO?.clientVO?.clientEmail || "N/A", // fallback for memberEmail
            merchantAddress:
              invoice.transferPartnerVO?.businessLocation || "N/A",
            merchantEmail:
              invoice.transferPartnerVO?.clientVO?.clientEmail || "N/A",
            paymentMethod: "N/A",
            subtotal: invoice.amount || 0,
            taxRate: invoice.taxPercentage || 0,
            tax: (invoice.amountAfterTaxes || 0) - (invoice.amount || 0),
            total: invoice.amountAfterTaxes || 0,
            startDate: invoice.startDate || null,
            endDate: invoice.endDate || null,
            transactions: [], // Not present in sample, keep empty
          };
        });
        setInvoices(transformedInvoices);
        setTotalInvoices(Number(response.data.totalNumberOfInvoices) || 0);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Failed to fetch invoices");
    } finally {
      setIsLoading(false);
    }
  };

  const totalPages = Math.ceil(totalInvoices / rowsPerPage);

  useEffect(() => {
    fetchInvoices();
  }, [currentPage, rowsPerPage, sortBy, filters, dateRange]);

  useEffect(() => {
    const fetchInvoiceStats = async () => {
      try {
        const response = await APIClient(
          "/invoice/getInvoiceStatisticsFromDB",
          { invoiceType: "burn" },
          {
            requestType: "post",
            baseUrl: import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL,
          }
        );
        if (response.data) {
          setInvoiceStats({
            totalInvoices: response.data.totalNumberOfInvoices || 0,
            totalPaid: formatCurrency(response.data.totalAmountPaid || 0),
            pendingAmount: formatCurrency(
              response.data.totalAmountPending || 0
            ),
            overdueAmount: formatCurrency(0), // Not provided in API, set to 0
            averagePaymentTime: 0, // Not provided in API, set to 0
            totalPaidInvoices: response.data.totalNumberOfPaidInvoices || 0,
            totalPendingInvoices:
              response.data.totalNumberOfPendingInvoices || 0,
            totalValueOfAllInvoices: formatCurrency(
              response.data.totalValueOfAllInvoices || 0
            ),
          });
        }
      } catch (error) {
        console.error("Error fetching invoice statistics:", error);
        toast.error("Failed to fetch invoice statistics");
      }
    };
    fetchInvoiceStats();
  }, []);

  useEffect(() => {
    async function fetchPrograms() {
      try {
        const response = await APIClient(
          "/program/getAllPrograms",
          {},
          {
            requestType: "get",
            baseUrl: import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL,
          }
        );
        const programList = Array.isArray(response.data)
          ? response.data.map((p) => ({
              programName: p.programName,
              programCode: p.programCode,
            }))
          : [];
        setPrograms(programList);
      } catch (err) {
        setPrograms([]);
        toast.error("Failed to fetch programs");
      }
    }
    fetchPrograms();
  }, []);

  useEffect(() => {
    async function fetchRedemptionMerchants() {
      try {
        const res = await fetch(
          `${
            import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL
          }/redemptionMerchant/getRedemptionMerchants`,
          {
            method: "POST",
            headers: {
              accept: "application/json, text/plain, */*",
              "content-type": "application/json",
            },
            body: JSON.stringify({
              merchantName: null,
              merchantCode: null,
              warehouseCountryId: null,
              pageNumber: 0,
              pageSize: 100,
              sortOrder: "desc",
              sortBy: "createdDate",
            }),
            credentials: "include",
          }
        );
        const data = await res.json();
        setRedemptionMerchants(data.redemptionMerchantVOS || []);
      } catch (e) {
        setRedemptionMerchants([]);
        toast.error("Failed to fetch redemption merchants");
      }
    }
    fetchRedemptionMerchants();
  }, []);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(0);
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
    setCurrentPage(0);
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  const handleReset = () => {
    setSearchTerm("");
    setFilters({});
    setDateRange("none");
    setSortBy({ field: "createdDate", direction: "desc" });
    toast.success("All filters have been reset");
  };

  const handleSort = (field) => {
    if (sortBy.field === field) {
      setSortBy({
        field,
        direction: sortBy.direction === "asc" ? "desc" : "asc",
      });
    } else {
      setSortBy({ field, direction: "asc" });
    }
  };

  const handleViewDetails = (invoice) => {
    setSelectedInvoice(invoice);
    setIsDetailsModalOpen(true);
  };

  const handleDownloadInvoice = async (invoiceId) => {
    const cleanInvoiceId = invoiceId.replace("#", "");
    const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;
    try {
      const response = await fetch(
        `${API_BASE}/invoice/v2/generateInvoicePDF?invoiceId=${cleanInvoiceId}`,
        {
          method: "GET",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to download PDF");
      }
      const filename = await downloadResponseFile(
        response,
        `invoice-${cleanInvoiceId}.pdf`
      );
      toast.success(`Invoice ${filename} downloaded successfully!`);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error(`Failed to download invoice ${invoiceId}`);
    }
  };

  const handleDownloadInvoiceCSV = async (invoiceId) => {
    const cleanInvoiceId = invoiceId.replace("#", "");
    const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;
    try {
      const response = await fetch(
        `${API_BASE}/invoice/getTransactionsFromInvoiceInCSVFormat?invoiceId=${cleanInvoiceId}`,
        {
          method: "GET",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to download CSV");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice-${cleanInvoiceId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Invoice CSV ${cleanInvoiceId} downloaded successfully!`);
    } catch (error) {
      console.error("Error downloading invoice CSV:", error);
      toast.error(`Failed to download invoice CSV ${invoiceId}`);
    }
  };

  const handleMarkAsPaid = async (invoiceId) => {
    try {
      // Remove # from invoice ID if present
      const cleanInvoiceId = invoiceId.replace("#", "");
      const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;
      const url = `${API_BASE}/invoice/updateInvoiceStatus?invoiceId=${cleanInvoiceId}&status=PAID`;
      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
      });
      if (response.ok) {
        toast.success(`Invoice ${invoiceId} marked as paid successfully!`);
        fetchInvoices();
      } else {
        const errorText = await response.text();
        toast.error(
          `Failed to mark invoice ${invoiceId} as paid: ${errorText}`
        );
      }
    } catch (error) {
      console.error("Error marking invoice as paid:", error);
      toast.error(`Failed to mark invoice ${invoiceId} as paid`);
    }
  };

  const SortableHeader = ({ field, children }) => {
    const isSorted = sortBy.field === field;
    const direction = sortBy.direction;
    return (
      <div
        className="flex items-center cursor-pointer"
        onClick={() => handleSort(field)}
      >
        {children}
        <span className="ml-1">
          {isSorted && direction === "asc" && <ArrowUp className="h-4 w-4" />}
          {isSorted && direction === "desc" && (
            <ArrowDown className="h-4 w-4" />
          )}
          {!isSorted && <ChevronRight className="h-4 w-4 opacity-30" />}
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Invoices</h1>
        <p className="text-gray-500 mt-1">
          Manage and view all invoices for your loyalty programs
        </p>
      </div>
      <InvoiceStats isLoading={isLoading} stats={invoiceStats} />
      <RewardPointInvoiceFilters
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
        onDateRangeChange={handleDateRangeChange}
        onReset={handleReset}
        programs={programs}
        redemptionMerchants={redemptionMerchants}
      />
      {isLoading ? (
        <InvoiceTableSkeleton rowCount={rowsPerPage} />
      ) : (
        <DataTable
          title="All Invoices"
          data={invoices}
          columns={[
            { header: "Invoice ID", accessorKey: "id" },
            {
              header: "Create Date",
              accessorKey: "createdDate",
              cell: (row) => (
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-gray-500" />
                  <span>{formatDate(row.createdDate)}</span>
                </div>
              ),
            },
            { header: "Invoice From", accessorKey: "merchant" },
            { header: "Invoice To", accessorKey: "program" },
            {
              header: "Issue Date",
              accessorKey: "issueDate",
              cell: (row) => (
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-gray-500" />
                  <span>{formatDate(row.issueDate)}</span>
                </div>
              ),
            },
            {
              header: <SortableHeader field="dueDate">Due Date</SortableHeader>,
              accessorKey: "dueDate",
              cell: (row) => (
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-gray-500" />
                  <span>{formatDate(row.dueDate)}</span>
                </div>
              ),
            },
            {
              header: <SortableHeader field="amount">Amount</SortableHeader>,
              accessorKey: "amount",
              cell: (row) => (
                <span className="font-medium">
                  {formatCurrency(row.amount)}
                </span>
              ),
            },
            {
              header: <SortableHeader field="status">Status</SortableHeader>,
              accessorKey: "status",
              cell: (row) => {
                const statusStyles = {
                  paid: "bg-green-100 text-green-800 hover:bg-green-100",
                  pending: "bg-yellow-100 text-yellow-800 hover:bg-yellow-100",
                  overdue: "bg-red-100 text-red-800 hover:bg-red-100",
                };
                return (
                  <Badge
                    variant="outline"
                    className={
                      statusStyles[row.status as keyof typeof statusStyles]
                    }
                  >
                    {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                  </Badge>
                );
              },
            },
            {
              header: "Actions",
              id: "actions",
              cell: (row) => (
                <div className="flex justify-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white">
                      <DropdownMenuItem onClick={() => handleViewDetails(row)}>
                        <Eye className="mr-2 h-4 w-4" />
                        <span>View Details</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDownloadInvoice(row.id)}
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        <span>Download invoice PDF</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDownloadInvoiceCSV(row.id)}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        <span>Download invoice CSV</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          row.status !== "paid" && handleMarkAsPaid(row.id)
                        }
                        disabled={row.status === "paid"}
                      >
                        <span className="mr-2 h-4 w-4">✓</span>
                        <span>Mark as Paid</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ),
            },
          ]}
        />
      )}
      {/* Pagination controls */}
      {!isLoading && totalInvoices > 0 && (
        <div className="mt-4 w-full">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Rows per page:</span>
              <Select
                value={rowsPerPage.toString()}
                onValueChange={(value) => {
                  setRowsPerPage(Number(value));
                  setCurrentPage(0);
                }}
              >
                <SelectTrigger className="w-[80px]">
                  <SelectValue placeholder={rowsPerPage.toString()} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 flex justify-center">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <button
                      className={`flex items-center justify-center h-9 w-9 rounded-md border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground ${
                        currentPage === 0
                          ? "pointer-events-none opacity-50"
                          : ""
                      }`}
                      onClick={() => setCurrentPage(0)}
                      disabled={currentPage === 0}
                      aria-label="First page"
                      type="button"
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </button>
                  </PaginationItem>
                  {currentPage > 0 ? (
                    <PaginationPrevious
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(0, prev - 1))
                      }
                      className="gap-0 px-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </PaginationPrevious>
                  ) : (
                    <PaginationPrevious
                      onClick={() => {}}
                      className="pointer-events-none opacity-50 gap-0 px-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </PaginationPrevious>
                  )}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNumber =
                      currentPage <= 2 ? i : currentPage + i - 2;
                    if (pageNumber >= totalPages) return null;
                    return (
                      <PaginationItem key={pageNumber}>
                        <PaginationLink
                          onClick={() => setCurrentPage(pageNumber)}
                          isActive={currentPage === pageNumber}
                        >
                          {pageNumber + 1}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  {currentPage < totalPages - 1 ? (
                    <PaginationNext
                      onClick={() =>
                        setCurrentPage((prev) =>
                          Math.min(totalPages - 1, prev + 1)
                        )
                      }
                      className="gap-0 px-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </PaginationNext>
                  ) : (
                    <PaginationNext
                      onClick={() => {}}
                      className="pointer-events-none opacity-50 gap-0 px-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </PaginationNext>
                  )}
                  <PaginationItem>
                    <button
                      className={`flex items-center justify-center h-9 w-9 rounded-md border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground ${
                        currentPage === totalPages - 1
                          ? "pointer-events-none opacity-50"
                          : ""
                      }`}
                      onClick={() => setCurrentPage(totalPages - 1)}
                      disabled={currentPage === totalPages - 1}
                      aria-label="Last page"
                      type="button"
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </button>
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
            <div className="w-[160px]" /> {/* Spacer to balance layout */}
          </div>
        </div>
      )}
      <InvoiceDetailsModal
        open={isDetailsModalOpen}
        onOpenChange={setIsDetailsModalOpen}
        invoice={selectedInvoice}
      />
    </div>
  );
};

export default RewardPointInvoices;
